import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Clock,
  Hourglass,
  Inbox,
  PauseCircle,
  RefreshCcw,
  Search,
} from "lucide-react";
import {
  errorMessage,
  getOutreach,
  type ContactEmailStatus,
  type EmailMeta,
  type OutreachCampaign,
  type OutreachItem,
  type OutreachKind,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CampaignContactSheet } from "@/components/email/CampaignContactSheet";
import { ContactStatusBadge, Hint, ScopeBadge, TonePill } from "@/components/email/EmailBadges";
import { useContactActions } from "@/components/email/useContactActions";
import { useDebounce } from "@/hooks/useDebounce";
import { useEmailProjects } from "@/hooks/useEmailProjects";
import {
  CONTACT_STATUS,
  CONTACT_STATUS_ORDER,
  OUTREACH_KIND,
  dayLabel,
  groupByDay,
  outreachWindow,
  shiftDay,
  todayIso,
} from "@/lib/email";
import { campaignPath } from "@/lib/emailRoutes";
import { formatDate, formatNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { useEmailOutlet } from "@/pages/email/EmailLayout";

const ANY = "__any__";
const ALL = "all";
const KINDS: OutreachKind[] = ["SCHEDULED", "OVERDUE", "SENT", "REPLIED", "BOUNCED", "UNSUBSCRIBED"];

interface Merged {
  items: (OutreachItem & { project_name: string })[];
  campaigns: (OutreachCampaign & { project_slug: string })[];
  waiting: number;
  held: number;
  overdue: number;
  truncated: boolean;
}

/**
 * Outreach: what goes out and comes back, across the project's campaigns,
 * day by day — read from Prism only, never from Lemlist. Past activity keeps
 * its real time; the next emails are an Estimated day (never an hour). Leads
 * without a date (not launched, paused) are counted, never placed. With
 * Email in several projects, "All" merges each project's own queue.
 */
export default function OutreachPage() {
  const { slug, meta } = useEmailOutlet();
  const { projects } = useEmailProjects();
  const [params, setParams] = useSearchParams();
  const today = todayIso();
  const view = params.get("view") === "week" ? "week" : "day";
  const anchor = /^\d{4}-\d{2}-\d{2}$/.test(params.get("date") ?? "") ? (params.get("date") as string) : today;
  const range = outreachWindow(view, anchor);
  const multi = projects.length > 1;
  const projectParam = params.get("project");
  const project =
    multi && projectParam && (projectParam === ALL || projects.some((p) => p.project.slug === projectParam))
      ? projectParam
      : slug;
  const campaignParam = params.get("campaign");
  const status = params.get("status") ?? ANY;
  const kind = params.get("kind") ?? ANY;
  const sender = params.get("sender") ?? ANY;
  const [search, setSearch] = useState(params.get("q") ?? "");
  const debounced = useDebounce(search, 250);
  const [data, setData] = useState<Merged | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [open, setOpen] = useState<{ slug: string; id: number } | null>(null);
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);
  const metaBySlug = useMemo(() => {
    const map = new Map<string, EmailMeta>();
    for (const p of projects) map.set(p.project.slug, p);
    if (meta) map.set(meta.project.slug, meta);
    return map;
  }, [projects, meta]);
  const slugs = project === ALL ? projects.map((p) => p.project.slug).join(",") : project;
  // A campaign filter names one project's campaign: `slug:id`.
  const [campaignSlug, campaignId] = campaignParam?.includes(":")
    ? [campaignParam.split(":")[0], Number(campaignParam.split(":")[1])]
    : [null, null];

  function update(next: Record<string, string | null>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === null || value === "" || value === ANY) merged.delete(key);
      else merged.set(key, value);
    }
    setParams(merged, { replace: true });
  }

  useEffect(() => {
    const current = params.get("q") ?? "";
    if (debounced.trim() !== current) update({ q: debounced.trim() || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the URL follows the debounced search only
  }, [debounced]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const targets = slugs.split(",").filter((s) => !campaignSlug || s === campaignSlug);
    Promise.all(
      targets.map((target) =>
        getOutreach(target, {
          start: range.start,
          end: range.end,
          tz: timezone,
          campaign_id: campaignSlug === target ? campaignId : null,
          status: status === ANY ? [] : [status as ContactEmailStatus],
          kinds: kind === ANY ? [] : [kind as OutreachKind],
          sender: sender === ANY ? null : sender,
          search: debounced.trim(),
        }).then((queue) => ({ target, queue })),
      ),
    )
      .then((answers) => {
        if (cancelled) return;
        const nameOf = (s: string) => metaBySlug.get(s)?.project.name ?? s;
        setData({
          items: answers
            .flatMap(({ target, queue }) => queue.items.map((item) => ({ ...item, project_name: nameOf(target) })))
            .sort((a, b) => a.day.localeCompare(b.day)),
          campaigns: answers.flatMap(({ target, queue }) =>
            queue.campaigns.map((c) => ({ ...c, project_slug: target })),
          ),
          waiting: answers.reduce((n, a) => n + a.queue.waiting_launch, 0),
          held: answers.reduce((n, a) => n + a.queue.on_hold, 0),
          overdue: answers.reduce((n, a) => n + a.queue.overdue, 0),
          truncated: answers.some((a) => a.queue.truncated),
        });
      })
      .catch((err) => !cancelled && setError(errorMessage(err, "The outreach could not be loaded.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- metaBySlug only names projects
  }, [slugs, range.start, range.end, timezone, campaignSlug, campaignId, status, kind, sender, debounced, reload]);

  const openMeta = open ? metaBySlug.get(open.slug) ?? null : null;
  const actions = useContactActions({
    external: !!openMeta?.external_effects,
    onChanged: () => setReload((n) => n + 1),
  });
  const groups = groupByDay(data?.items ?? []);
  const senders = [...new Set((data?.campaigns ?? []).map((c) => c.sender).filter((s): s is string => !!s))];
  const label =
    view === "day"
      ? dayLabel(range.start, today)
      : `${formatDate(range.start, "d MMM")} – ${formatDate(range.end, "d MMM yyyy")}`;
  const filtered = status !== ANY || kind !== ANY || sender !== ANY || !!campaignParam || !!debounced.trim();

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div role="radiogroup" aria-label="Range" className="inline-flex rounded-lg bg-white p-0.5 shadow-sm-tile ring-1 ring-slate-900/[0.04]">
          {(["day", "week"] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={view === value}
              onClick={() => update({ view: value === "day" ? null : value })}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors",
                view === value ? "bg-indigo-600 text-white shadow-cta" : "text-slate-500 hover:text-slate-800",
              )}
            >
              {value}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-9 w-9" aria-label={`Previous ${view}`} onClick={() => update({ date: shiftDay(view, anchor, -1) })}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => update({ date: null })} disabled={anchor === today}>
            Today
          </Button>
          <Button variant="outline" size="icon" className="h-9 w-9" aria-label={`Next ${view}`} onClick={() => update({ date: shiftDay(view, anchor, 1) })}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <p className="min-w-[150px] text-sm font-semibold text-slate-800">{label}</p>
        <Button variant="ghost" size="sm" className="h-9 text-xs text-slate-500" onClick={() => setReload((n) => n + 1)} aria-label="Refresh">
          <RefreshCcw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
        </Button>
        <div className="ml-auto flex flex-wrap items-center gap-2 text-xs">
          {data && data.overdue > 0 && (
            <Hint content={OUTREACH_KIND.OVERDUE.hint}>
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-medium text-amber-800">
                <AlertTriangle className="h-3 w-3" /> {formatNumber(data.overdue)} overdue
              </span>
            </Hint>
          )}
          {data && (
            <Hint content="Leads added but not launched: they get no date until someone launches them (Launch tab).">
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-slate-600">
                <Hourglass className="h-3 w-3" /> {formatNumber(data.waiting)} waiting for launch
              </span>
            </Hint>
          )}
          {data && (
            <Hint content="Launched leads paused, or whose campaign is paused: nothing is sent, so no date.">
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-slate-600">
                <PauseCircle className="h-3 w-3" /> {formatNumber(data.held)} on hold
              </span>
            </Hint>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-plane bg-white p-3 shadow-plane">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search a lead or a company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 rounded-lg border-slate-200 bg-slate-50/60 pl-9"
            aria-label="Search the outreach"
          />
        </div>
        {multi && (
          <Select value={project} onValueChange={(value) => update({ project: value === slug ? null : value, campaign: null })}>
            <SelectTrigger className="h-9 w-[170px] rounded-lg border-slate-200 bg-white text-xs" aria-label="Project">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All projects</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.project.slug} value={p.project.slug}>
                  <span className="inline-flex items-center gap-1.5">
                    <ScopeBadge scope={p.project.scope} />
                    {p.project.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={campaignParam ?? ANY} onValueChange={(value) => update({ campaign: value })}>
          <SelectTrigger className="h-9 w-[190px] rounded-lg border-slate-200 bg-white text-xs" aria-label="Campaign">
            <SelectValue placeholder="All campaigns" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All campaigns</SelectItem>
            {(data?.campaigns ?? []).map((c) => (
              <SelectItem key={`${c.project_slug}:${c.id}`} value={`${c.project_slug}:${c.id}`}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={kind} onValueChange={(value) => update({ kind: value })}>
          <SelectTrigger className="h-9 w-[150px] rounded-lg border-slate-200 bg-white text-xs" aria-label="Activity">
            <SelectValue placeholder="Any activity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any activity</SelectItem>
            {KINDS.map((k) => (
              <SelectItem key={k} value={k}>
                {OUTREACH_KIND[k].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(value) => update({ status: value })}>
          <SelectTrigger className="h-9 w-[150px] rounded-lg border-slate-200 bg-white text-xs" aria-label="Lead state">
            <SelectValue placeholder="Any state" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any state</SelectItem>
            {CONTACT_STATUS_ORDER.map((s) => (
              <SelectItem key={s} value={s}>
                {CONTACT_STATUS[s].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {senders.length > 0 && (
          <Select value={sender} onValueChange={(value) => update({ sender: value })}>
            <SelectTrigger className="h-9 w-[190px] rounded-lg border-slate-200 bg-white text-xs" aria-label="Sender">
              <SelectValue placeholder="Any sender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any sender</SelectItem>
              {senders.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {error ? (
        <div className="flex items-center gap-2 rounded-plane bg-white p-6 text-sm text-rose-600 shadow-plane">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      ) : !data ? (
        <div className="space-y-2 rounded-plane bg-white p-4 shadow-plane" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full rounded-lg" />
          ))}
        </div>
      ) : !groups.length ? (
        <div className="flex flex-col items-center rounded-plane bg-white px-6 py-16 text-center shadow-plane">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-500">
            <Inbox className="h-5 w-5" />
          </span>
          <p className="mt-3 text-sm font-semibold text-slate-800">
            {filtered ? "Nothing matches on these days" : view === "day" ? "Nothing on this day" : "Nothing this week"}
          </p>
          <p className="mt-1 max-w-sm text-xs text-slate-500">
            Sends, replies and the next estimated emails of launched leads show here. Leads waiting for launch or on
            hold have no date.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <section key={group.day} className="overflow-hidden rounded-plane bg-white shadow-plane">
              <header className="flex items-center justify-between border-b border-slate-100 bg-slate-50/70 px-4 py-2">
                <h3 className={cn("text-sm font-semibold", group.day === today ? "text-indigo-700" : "text-slate-800")}>
                  {dayLabel(group.day, today)}
                </h3>
                <span className="text-xs tabular-nums text-slate-400">
                  {formatNumber(group.items.length)} {group.items.length === 1 ? "activity" : "activities"}
                </span>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] table-fixed text-sm">
                  <colgroup>
                    <col className="w-[170px]" />
                    <col className="w-[27%]" />
                    <col />
                    <col className="w-[140px]" />
                  </colgroup>
                  <tbody>
                    {group.items.map((item) => (
                      <tr
                        key={`${item.project_slug}-${item.kind}-${item.membership_id}-${item.step_position}-${item.at ?? item.day}`}
                        onClick={() => setOpen({ slug: item.project_slug, id: item.membership_id })}
                        className="cursor-pointer border-b border-slate-50 align-top transition-colors last:border-0 hover:bg-indigo-50/30"
                      >
                        <td className="whitespace-nowrap px-4 py-2">
                          <TonePill tone={OUTREACH_KIND[item.kind]} />
                          <p className="mt-1 text-[11px]">
                            {item.estimated ? (
                              <Hint content={OUTREACH_KIND[item.kind].hint}>
                                <span
                                  className={cn(
                                    "inline-flex items-center gap-1 font-medium",
                                    item.kind === "OVERDUE" ? "text-amber-700" : "text-sky-700",
                                  )}
                                >
                                  <CalendarClock className="h-3 w-3" />
                                  {item.kind === "OVERDUE" && item.estimated_day
                                    ? `Estimated ${formatDate(item.estimated_day, "EEE d MMM")}`
                                    : "Estimated · no set hour"}
                                </span>
                              </Hint>
                            ) : (
                              <span className="inline-flex items-center gap-1 tabular-nums text-slate-500">
                                <Clock className="h-3 w-3 text-slate-400" />
                                {item.at ? formatDate(item.at, "HH:mm") : "—"}
                              </span>
                            )}
                          </p>
                        </td>
                        <td className="px-3 py-2">
                          <p className="truncate font-medium text-slate-900">{item.contact_name}</p>
                          <p className="truncate text-xs text-slate-500">
                            {[item.contact_title, item.company_name].filter(Boolean).join(" · ") || "—"}
                          </p>
                        </td>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <Link
                            to={campaignPath(item.project_slug, item.campaign_id, "leads", { lead: item.membership_id })}
                            className="block truncate text-slate-700 hover:text-indigo-700"
                          >
                            {item.campaign_name}
                          </Link>
                          <p className="flex items-center gap-1 truncate text-xs text-slate-500">
                            {project === ALL && <ScopeBadge scope={item.scope} className="px-1 py-0 text-[9px]" />}
                            {item.step_position ? (
                              <span className="truncate">
                                Step {item.step_position}
                                {item.step_name ? ` · ${item.step_name}` : ""}
                              </span>
                            ) : (
                              <span className="text-slate-300">No step</span>
                            )}
                          </p>
                        </td>
                        <td className="px-3 py-2">
                          <ContactStatusBadge status={item.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
          {data.truncated && (
            <p className="text-center text-xs text-slate-400">More activity than shown: narrow the days or the filters.</p>
          )}
          <p className="px-1 text-xs text-slate-400">
            Times are yours ({timezone}); an estimated day is in its campaign&apos;s timezone and never has an hour —
            Lemlist chooses it within the sending window.
          </p>
        </div>
      )}

      {open && openMeta && (
        <CampaignContactSheet
          slug={open.slug}
          meta={openMeta}
          membershipId={open.id}
          onOpenChange={(value) => !value && setOpen(null)}
          onAction={async (action, row) => {
            if (action === "move") await actions.move([row]);
            else await actions.act(action, [row]);
          }}
        />
      )}
      {actions.dialogs}
    </div>
  );
}
